import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Sparkles, RefreshCw, TrendingUp, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ThinkingLoader from "@/components/ThinkingLoader";

/**
 * MatchInsightCard — inline "why you fit" verdict shown inside JobDetailSheet.
 *
 * Behaviour:
 *  - On mount → `GET /api/jobs/{id}/match-insight`. If a cached insight exists
 *    on the job document it renders immediately (zero LLM cost). Otherwise the
 *    endpoint generates + caches on first read.
 *  - Manual "Regenereer" button posts to the same route so the user can force
 *    a refresh after editing the CV or vacancy description.
 *  - Verdict → color-coded pill + score bar. Strengths render as green ticks,
 *    gaps as amber warnings. Card stays hidden when the job has no
 *    description (nothing to analyse) so the sheet doesn't show a broken CTA.
 */
const VERDICT_STYLES = {
  strong: { bar: "bg-emerald-600", pill: "bg-emerald-700 text-[#F9F8F6]" },
  good:   { bar: "bg-lime-600",    pill: "bg-lime-700 text-[#F9F8F6]" },
  mixed:  { bar: "bg-amber-500",   pill: "bg-amber-600 text-[#F9F8F6]" },
  weak:   { bar: "bg-red-500",     pill: "bg-red-600 text-[#F9F8F6]" },
};

export default function MatchInsightCard({ jobId, hasDescription = true }) {
  const { t } = useT();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchInsight = useCallback(async (force = false) => {
    if (!jobId || !hasDescription) return;
    setError(null);
    if (force) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const url = `/jobs/${jobId}/match-insight${force ? "?refresh=true" : ""}`;
      const res = force ? await api.post(url) : await api.get(url);
      setInsight(res.data?.insight || null);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      setError(detail || t("miErrGeneric"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [jobId, hasDescription, t]);

  useEffect(() => {
    fetchInsight(false);
  }, [fetchInsight]);

  if (!hasDescription) return null;

  return (
    <section data-testid="match-insight-card" className="mt-6">
      <p className="ed-eyebrow mb-4 flex items-center gap-2">
        <Sparkles size={12} strokeWidth={1.75} />
        {t("miEyebrow")}
      </p>
      <div className="ed-card p-6">
        {loading && !insight ? (
          <div data-testid="match-insight-loading" className="space-y-3">
            <div className="mb-3">
              <ThinkingLoader stage="insight" testId="match-insight-thinking" />
            </div>
            <div className="animate-pulse space-y-3">
              <div className="h-3 bg-neutral-200 w-24" />
              <div className="h-6 bg-neutral-200 w-3/4" />
              <div className="h-3 bg-neutral-200 w-full" />
              <div className="h-3 bg-neutral-200 w-2/3" />
            </div>
          </div>
        ) : error ? (
          <div data-testid="match-insight-error" className="text-sm text-neutral-600 flex items-start gap-2">
            <AlertCircle size={14} strokeWidth={1.5} className="text-amber-600 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : insight ? (
          <>
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-3">
                <span
                  data-testid="match-insight-score"
                  className="font-serif text-4xl leading-none"
                >
                  {insight.score}
                </span>
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">/ 100</span>
                <span
                  data-testid="match-insight-verdict"
                  className={`font-mono text-[0.6rem] uppercase tracking-[0.15em] px-2 py-1 ${VERDICT_STYLES[insight.verdict]?.pill || VERDICT_STYLES.mixed.pill}`}
                >
                  {t(`miVerdict_${insight.verdict}`)}
                </span>
              </div>
              <button
                data-testid="match-insight-refresh"
                type="button"
                onClick={() => fetchInsight(true)}
                disabled={refreshing}
                className="font-mono text-[0.6rem] uppercase tracking-[0.15em] px-2 py-1 hover:bg-neutral-100 disabled:opacity-40 inline-flex items-center gap-1"
                title={t("miRefreshHint")}
              >
                <RefreshCw size={11} strokeWidth={1.75} className={refreshing ? "animate-spin" : ""} />
                {t("miRefreshBtn")}
              </button>
            </div>
            <div className="w-full h-1.5 bg-neutral-100 mb-5">
              <div
                data-testid="match-insight-bar"
                className={`h-1.5 ${VERDICT_STYLES[insight.verdict]?.bar || VERDICT_STYLES.mixed.bar} transition-all duration-500`}
                style={{ width: `${Math.max(2, Math.min(100, insight.score))}%` }}
              />
            </div>
            <p
              data-testid="match-insight-summary"
              className="font-serif text-base leading-relaxed text-neutral-800"
            >
              {insight.summary}
            </p>
            {insight.strengths?.length > 0 && (
              <div className="mt-5" data-testid="match-insight-strengths">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2 flex items-center gap-1.5">
                  <TrendingUp size={11} strokeWidth={1.5} />
                  {t("miStrengthsHeader")}
                </p>
                <ul className="space-y-1.5">
                  {insight.strengths.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-neutral-800">
                      <CheckCircle2 size={13} strokeWidth={1.75} className="text-emerald-600 mt-0.5 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {insight.gaps?.length > 0 && (
              <div className="mt-4" data-testid="match-insight-gaps">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2 flex items-center gap-1.5">
                  <AlertCircle size={11} strokeWidth={1.5} />
                  {t("miGapsHeader")}
                </p>
                <ul className="space-y-1.5">
                  {insight.gaps.map((g, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-neutral-700">
                      <AlertCircle size={13} strokeWidth={1.75} className="text-amber-600 mt-0.5 shrink-0" />
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {insight.generated_at && (
              <p className="mt-5 font-mono text-[0.55rem] text-neutral-400 uppercase tracking-[0.15em]">
                {t("miGeneratedAt")} {new Date(insight.generated_at).toLocaleString()}
              </p>
            )}
          </>
        ) : null}
      </div>
    </section>
  );
}
